import React, { useEffect, useRef } from 'react';
import MailListItem from './MailListItem';
import MailListSkeleton from './MailListSkeleton';

const OUTGOING_TABS = ['sent', 'replied', 'forwarded', 'scheduled'];

/**
 * MailList — the scrollable left pane: rendered list of preview rows plus a
 * seamless infinite-scroll loader and the loading / empty states.
 */
export default function MailList({
  emails,
  selectedTab,
  selectedEmailId,
  loading,
  loadingMore,
  hasMore,
  onSelect,
  onLoadMore,
  isBlockedSender,
}) {
  const isOutgoing = OUTGOING_TABS.includes(selectedTab);

  // ── Infinite scroll ──────────────────────────────────────────────
  // A sentinel sits below the last row; when it scrolls into view we pull the
  // next page automatically (no "Load more" click). We only observe while idle
  // so loads can't pile up, and we re-arm after every change — that way, when a
  // freshly loaded page is too short to push the sentinel back off-screen
  // (e.g. a stretch dominated by hidden warmup mail), the observer fires again
  // and keeps filling until a real screenful is reached or there's nothing left.
  const sentinelRef = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMoreRef.current?.();
      },
      // Prefetch a little before the sentinel is actually on screen.
      { rootMargin: '350px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, emails.length]);

  // Zero rows while the source still has pages left means the data hook is
  // paging past a stretch of hidden mail. That's loading, not empty — showing
  // the empty state here is what used to make a full mailbox look deserted.
  if (loading || (!emails.length && (hasMore || loadingMore))) {
    return <MailListSkeleton />;
  }

  if (!emails.length) {
    return (
      <div className="ivx-list-empty">
        <img src="/img/mailbox-empty.svg" alt="" className="ivx-list-empty-img"
          onError={(e) => { e.target.style.display = 'none'; }} />
        <p>{selectedTab === 'leads'
          ? 'No mail from your leads yet'
          : selectedTab === 'bounced'
            ? 'No undeliverable / bounce notices'
            : 'No emails here'}</p>
      </div>
    );
  }

  return (
    <div className="ivx-list">
      {emails.map((email) => (
        <MailListItem
          key={email.id}
          email={email}
          isActive={email.id === selectedEmailId}
          isOutgoing={isOutgoing}
          onSelect={onSelect}
          isBlockedSender={isBlockedSender}
        />
      ))}

      {hasMore && (
        <div className="ivx-list-sentinel" ref={sentinelRef}>
          <span className={`ivx-list-spinner ${loadingMore ? 'visible' : ''}`} aria-hidden="true" />
        </div>
      )}
    </div>
  );
}
